<script setup>
import { computed } from 'vue';

const props = defineProps({
  icon: { type: String, default: '' },
  type: { type: String, default: 'button' },
  tag: {
    type: String,
    default: 'button',
    validator: v => ['button', 'a'].includes(v),
  },
  href: { type: String, default: '' },
  target: { type: String, default: '' },
  rel: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  variant: {
    type: String,
    default: 'primary',
    validator: v => ['primary', 'ghost', 'link'].includes(v),
  },
  size: {
    type: String,
    default: 'md',
    validator: v => ['sm', 'md', 'lg'].includes(v),
  },
});

const emit = defineEmits(['click']);

defineOptions({
  inheritAttrs: false,
});

const isButton = computed(() => props.tag === 'button');
const isDisabled = computed(() => props.isLoading || props.disabled);
const resolvedRel = computed(() => {
  if (props.rel) return props.rel;
  return props.target === '_blank' ? 'noopener noreferrer' : undefined;
});

const handleClick = event => {
  if (isDisabled.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  emit('click', event);
};
</script>

<template>
  <component
    :is="tag"
    v-bind="$attrs"
    :type="isButton ? type : undefined"
    :href="!isButton && !isDisabled ? href : undefined"
    :target="!isButton && target ? target : undefined"
    :rel="!isButton ? resolvedRel : undefined"
    :disabled="isButton ? isDisabled : undefined"
    :aria-disabled="!isButton && isDisabled ? 'true' : undefined"
    :aria-busy="isLoading ? 'true' : undefined"
    :tabindex="!isButton && isDisabled ? -1 : undefined"
    class="inline-flex min-h-9 items-center justify-center gap-2 rounded-lais font-redHatText font-semibold leading-none transition-all focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-50"
    :class="{
      // primary
      'bg-lais-primary text-white hover:bg-lais-primary-600 shadow-lais-soft':
        variant === 'primary' && !isDisabled,
      'bg-lais-primary text-white shadow-lais-soft':
        variant === 'primary' && isDisabled,
      // ghost
      'bg-white text-lais-text border border-lais-border hover:border-lais-primary hover:bg-lais-primary-50/30':
        variant === 'ghost' && !isDisabled,
      'bg-white text-lais-text border border-lais-border':
        variant === 'ghost' && isDisabled,
      // link
      'text-lais-link hover:text-lais-link-hover bg-transparent':
        variant === 'link' && !isDisabled,
      'text-lais-text-muted bg-transparent': variant === 'link' && isDisabled,
      // sizes
      'text-[13px] px-3 py-1.5': size === 'sm',
      'text-[14px] px-4 py-2.5': size === 'md',
      'text-[15px] px-6 py-3': size === 'lg',
    }"
    @click="handleClick"
  >
    <span
      v-if="icon && !isLoading"
      class="shrink-0"
      :class="[size === 'sm' ? 'size-3.5' : 'size-4', icon]"
      aria-hidden="true"
    />
    <span
      v-if="isLoading"
      class="i-lucide-loader-circle size-4 animate-spin"
      aria-hidden="true"
    />
    <span><slot /></span>
  </component>
</template>
